<template>
    <div class="square-crop-upload">
        <!-- 触发按钮 -->
        <el-upload
            ref="upload"
            action=""
            :show-file-list="false"
            :before-upload="beforeRead"
            accept="image/*"
        >
            <el-button size="small" type="primary" icon="el-icon-picture">选择图片</el-button>
        </el-upload>

        <!-- 裁剪弹窗 -->
        <el-dialog
            title="裁剪图片"
            :visible.sync="dialog"
            width="420px"
            @opened="onOpened"
            :close-on-click-modal="false"
            @close="closeDialog"
            modal-class="low-modal"
        >
            <div v-if="showCropper" class="crop-box">
                <vue-cropper
                    :key="cropKey"
                    ref="cropper"
                    :img="cropImg"
                    :output-size="1"
                    :fixed="true"
                    :fixed-number="[1,1]"
                    :center-box="true"
                    :info-true="true"
                    :auto-crop="true"
                    :fixed-box="false"
                />
            </div>
            <span slot="footer">
        <el-button @click="dialog = false">取消</el-button>
        <el-button type="primary" @click="finish">确认</el-button>
      </span>
        </el-dialog>

        <!-- 预览 -->
        <div v-if="preview" class="preview-box">
            <img :src="preview"/>
            <i class="el-icon-delete" @click="remove"></i>
        </div>
    </div>
</template>

<script>
import global from "../../../api/global";
import { reqUploadFile } from '@/api/system'

export default {
    name: 'SquareCropUpload',
    props: {
        /* 限制大小 KB */
        maxSize: {
            type: Number,
            default: 2048
        },
        /* 编辑时传入的远程地址 */
        value: {              // 支持 v-model
            type: String,
            default: ''
        },
        /* 是否自动上传到服务器 */
        autoUpload: {
            type: Boolean,
            default: true
        }
    },
    watch: {
        value: {
            handler(v) {
                // ✅ 修复：当值变化时始终更新，而不是只在 cropImg 为空时更新
                if (v) {
                    this.remoteUrl = v
                    // 如果父级没给完整 http，就自动拼前缀
                    const fullUrl = v.startsWith('http') ? v : this.filePre + v
                    this.cropImg = fullUrl
                    this.preview = fullUrl
                } else {
                    // 当值为空时，清空预览
                    this.remoteUrl = ''
                    this.cropImg = ''
                    this.preview = ''
                }
            },
            immediate: true
        }
    },
    data() {
        return {
            dialog: false,
            cropImg: '',
            preview: '',      // base64
            remoteUrl: '',    // 记录远程地址，用于"删除"时对比
            fileName: '',
            showCropper: false,
            cropKey: 0,
            filePre: global.preview_path_url,
            uploading: false  // 上传状态
        }
    },
    methods: {
        // 读取文件
        beforeRead(file) {
            const isImage = file.type.startsWith('image/')
            const isLt = file.size / 1024 < this.maxSize
            if (!isImage) {
                this.$message.warning('只能上传图片')
                return false
            }
            if (!isLt) {
                this.$message.warning(`图片大小不能超过 ${this.maxSize}KB`)
                return false
            }
            this.remoteUrl = '';
            this.fileName = file.name
            const reader = new FileReader()
            reader.onload = e => {
                this.cropImg = e.target.result
                this.dialog = true
                this.showCropper = false
            }
            reader.readAsDataURL(file)
            return false   // 阻止 el-upload 自动上传
        },
        // 裁剪完成
        async finish() {
            this.$refs.cropper.getCropBlob(async blob => {
                try {
                    if (this.autoUpload) {
                        // ✅ 自动上传到服务器
                        this.uploading = true
                        const formData = new FormData()
                        formData.append('file', blob, this.fileName || 'image.jpg')

                        const res = await reqUploadFile(formData)
                        if (res) {
                            // res 可能是 FileUploadResult 对象 {url, key, ...} 或纯字符串
                            const url = typeof res === 'string' ? res : (res.url || res.key || '')
                            if (!url) {
                                this.$message.error('图片上传失败：未获取到文件地址')
                                return
                            }
                            // 更新 v-model
                            this.$emit('input', url)

                            // 本地预览
                            const fullUrl = url.startsWith('http') ? url : this.filePre + url
                            this.preview = fullUrl
                            this.remoteUrl = url

                            this.$message.success('图片上传成功')
                        } else {
                            this.$message.error('图片上传失败')
                        }
                    } else {
                        // 不自动上传，触发 success 事件让父组件处理
                        this.preview = URL.createObjectURL(blob)
                        this.$refs.cropper.getCropData(base64 => {
                            this.$emit('success', {blob, base64, name: this.fileName})
                        })
                    }
                    this.dialog = false
                } catch (error) {
                    console.error('图片上传失败', error)
                    this.$message.error('图片上传失败: ' + (error.message || '未知错误'))
                } finally {
                    this.uploading = false
                }
            })
        },
        remove() {
            this.preview = ''
            this.cropImg = ''
            this.remoteUrl = ''
            this.$emit('input', '')   // 双向清空
            this.$emit('remove')
        },
        closeDialog() {
            this.cropImg = ''
            this.showCropper = false
            this.$refs.cropper.clearCrop()
        },
        onOpened() {
            this.cropKey += 1;
            this.$nextTick(() => {
                this.showCropper = true
                // 保险：等动画 30ms 后再通知 cropper 重新计算
                setTimeout(() => {
                    this.$refs.cropper && this.$refs.cropper.refresh();
                }, 30);
            })
        },
    }
}
</script>

<style lang="scss" scoped>
.square-crop-upload {
    display: inline-block;
}

.crop-box {
    width: 380px;
    height: 320px;
    border: 1px solid red;
}

.preview-box {
    position: relative;
    display: inline-block;
    margin-left: 20px;

    img {
        width: 100px;
        height: 100px;
        border-radius: 4px;
        object-fit: cover;
    }

    i {
        position: absolute;
        top: -8px;
        right: -8px;
        cursor: pointer;
        background: #fff;
        border-radius: 50%;
        color: #f56c6c;
        font-size: 18px;
        box-shadow: 0 2px 4px rgba(0, 0, 0, .2);
    }
}
</style>
